import type { ContractKind } from '@repo/database'
import type { ContractStageMap } from './contract-process'

/** Druhy zmlúv v poradí, v akom ich proces používa (spec zmlúv §5.1). */
export const CONTRACT_KINDS = [
  'RESERVATION',
  'RESERVATION_AMENDMENT',
  'EXCLUSIVE',
  'EXCLUSIVE_AMENDMENT',
  'FUTURE_PURCHASE',
] as const satisfies readonly ContractKind[]

export type ContractKindValue = (typeof CONTRACT_KINDS)[number]

export const CONTRACT_DEADLINE_MIN_DAYS = 1
export const CONTRACT_DEADLINE_MAX_DAYS = 60

/**
 * Lehoty procesu v kalendárnych dňoch (spec §4.4) — jeden zoznam pre API (validácia, výpočet termínov)
 * aj admin (formulár lehôt). `from` = od čoho sa lehota počíta.
 */
export const CONTRACT_DEADLINE_FIELDS = [
  { key: 'rzValidityDays', label: 'Platnosť rezervačnej zmluvy', from: 'od podpisu RZ', defaultDays: 15 },
  { key: 'depositDueDays', label: 'Pripísanie rezervačnej zálohy', from: 'od podpisu RZ', defaultDays: 3 },
  { key: 'requestDueDays', label: 'Odoslanie požiadavky na ZOBKZ právnikovi', from: 'od podpisu RZ', defaultDays: 4 },
  { key: 'vzDueDays', label: 'Podpísaná výhradná zmluva alebo dodatok', from: 'od podpisu RZ', defaultDays: 4 },
  { key: 'draftDueDays', label: 'Návrh ZOBKZ pripravený', from: 'od podpisu RZ', defaultDays: 7 },
  { key: 'lawyerDays', label: 'Spracovanie požiadavky právnikom', from: 'od odoslania požiadavky', defaultDays: 3 },
  { key: 'rzExpiryWarningDays', label: 'Upozornenie pred koncom platnosti RZ', from: 'dní vopred', defaultDays: 3 },
] as const

export type ContractDeadlineKey = (typeof CONTRACT_DEADLINE_FIELDS)[number]['key']
export type ContractDeadlines = Record<ContractDeadlineKey, number>

export const CONTRACT_DEADLINE_DEFAULTS = Object.fromEntries(
  CONTRACT_DEADLINE_FIELDS.map((f) => [f.key, f.defaultDays]),
) as ContractDeadlines

const isDeadlineDays = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= CONTRACT_DEADLINE_MIN_DAYS && (value as number) <= CONTRACT_DEADLINE_MAX_DAYS

/** Lehoty z `tenant.settings.contractDeadlines`; chýbajúca alebo neplatná hodnota = predvolená. */
export function resolveContractDeadlines(settings: unknown): ContractDeadlines {
  const stored = (settings as { contractDeadlines?: Record<string, unknown> } | null)?.contractDeadlines ?? {}
  const resolved = { ...CONTRACT_DEADLINE_DEFAULTS }
  for (const { key } of CONTRACT_DEADLINE_FIELDS) {
    if (isDeadlineDays(stored[key])) resolved[key] = stored[key] as number
  }
  return resolved
}

/** Garančný účet na rezervačné zálohy (spec §5.3). */
export interface GuaranteeAccount {
  iban: string
  name: string
}

/** `GET/PATCH /contracts/settings` (OWNER+). Obchodné meno, IČO a sídlo firmy sú v Nastavenia → Všeobecné; mapovanie milníkov sa upravuje v Nastavenia → Pipeline. */
export interface ContractSettingsDto {
  companyStatutory: string
  guaranteeAccount: GuaranteeAccount | null
  contractDeadlines: ContractDeadlines
  /** Štádium pipeline pre milník procesu (spec §4.6); chýbajúci milník obchod nepresúva. */
  contractStageMap: ContractStageMap
}

/** Uzol definície značiek šablón (`GET /contracts/templates/tags`); API podľa nej aj kontroluje nahraté šablóny. */
export type ContractTagField =
  | { type: 'text'; label: string; example: string }
  | { type: 'flag'; label: string; example: boolean }
  | { type: 'object'; label: string; fields: ContractTagFields }
  | { type: 'list'; label: string; item: ContractTagFields }

export type ContractTagFields = Record<string, ContractTagField>

/** Šablóna v zozname (`GET /contracts/templates`). */
export interface ContractTemplateDto {
  id: string
  kind: ContractKindValue
  name: string
  propertyTypes: string[]
  fileName: string
  tags: string[]
  createdBy: { id: string; firstName: string; lastName: string } | null
  createdAt: string
}

/** `POST /contracts/templates`: nová šablóna a názvy šablón, ktoré nahradila alebo ktorým ubrala typy nehnuteľností. */
export interface UploadContractTemplateResult {
  template: ContractTemplateDto
  replaced: string[]
}

export const CONTRACT_TEMPLATE_MAX_BYTES = 10 * 1024 * 1024
